/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import {
  Box,
  Chip,
  ConfirmationDialogBox,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  ClipboardCheckIcon,
  EmailIcon,
  MagnifyIcon,
  OpenInNewIcon,
  PhoneIcon,
  StorefrontIcon,
} from '@atgfr8/icons';
import { countryTypes, currencyTypes } from '@common/commonLists';
import {
  AutoComplete,
  ContactDropdown,
  SelectInput,
  ToastType,
  PhoneInput,
} from '@components';
import AddressAutoComplete from '@components/Autocomplete/AddressAutoComplete';
import { Button } from '@components/Button';
import { SelectInputCustom } from '@components/Select';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { UserRole } from '@mobileStore/user/types';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import {
  clearVendorDraftFormState,
  orderStore,
  updateOrderFormErrorState,
  updateVendorDraftFormState,
  updateVendorPropertyAtIndex,
  vendor,
} from '@store/orders';
import { CarrierContactT } from '@store/orders/types';
import {
  fetchVendorServicesList,
  saveNewVendorDetails,
  searchVendors,
} from '@store/vendors/vendorDetails/services';
import {
  VendorDetails,
  VendorSearchContactResult,
  VendorSearchResult,
  VendorSearchServiceTypeResult,
  VendorServiceTypeList,
} from '@store/vendors/vendorDetails/types';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { sanitizeStateInput } from '@utils/formUtils';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { printLog } from '@utils/printUtils';
import { handleToast, userHasRole } from '@utils/utils';
import { LocationSelect } from '@views/carrierMatching/types';
import { vendorStatusTypes } from '@views/order/constants';
import { batch, createEffect, createSignal, on, onMount, Show } from 'solid-js';
import * as yup from 'yup';

import { VendorSearchResultGridOnLoad } from './VendorSearchResultGridOnLoad';

type CreateEditVendorFormProps = {
  index: number;
};

export const CreateEditVendorForm = (props: CreateEditVendorFormProps) => {
  const [showVendorForm, setShowVendorForm] = createSignal(false);
  const [selectedServiceTypes, setSelectedServiceTypes] = createSignal<
    number[]
  >([]);
  const [vendorResults, setVendorResults] = createSignal<VendorSearchResult[]>(
    [],
  );
  const [isSearching, setIsSearching] = createSignal(false);
  const [hasSearched, setHasSearched] = createSignal(false);
  const [vendorServiceTypes, setVendorServiceTypes] = createSignal<
    VendorServiceTypeList[]
  >([]);
  const [hasSelectedAddress, setHasSelectedAddress] = createSignal(false);
  const [localVendorName, setLocalVendorName] = createSignal('');
  const [savingNewVendor, setSavingNewVendor] = createSignal(false);
  const [manuallyEnteringAddress, setManuallyEnteringAddress] =
    createSignal(false);
  const [savedVendorSelected, setSavedVendorSelected] = createSignal(false);
  const [selectedVendorContacts, setSelectedVendorContacts] = createSignal<
    VendorSearchContactResult[]
  >([]);
  const [selectedContact, setSelectedContact] =
    createSignal<VendorSearchContactResult | null>(null);
  const [selectedVendorServiceTypes, setSelectedVendorServiceTypes] =
    createSignal<VendorSearchServiceTypeResult[]>([]);
  const [selectedServiceType, setSelectedServiceType] =
    createSignal<string>('');

  onMount(async () => {
    const response = await fetchVendorServicesList();
    if (response?.value) {
      setVendorServiceTypes(response.value);
    }
  });

  const mapCountryCodeForAddressAutocomplete: Record<string, string> = {
    'United States': 'USA',
    Canada: 'CAN',
    Mexico: 'MEX',
  };

  const countries = [
    {
      label: 'USA',
      value: 'US',
    },
    {
      label: 'CAN',
      value: 'CA',
    },
    {
      label: 'MEX',
      value: 'MEX',
    },
  ];

  const felteFieldError = (v: unknown): string | undefined => {
    if (v == null) return undefined;
    if (Array.isArray(v))
      return v.length ? v.filter(Boolean).join(', ') : undefined;
    return typeof v === 'string' ? v : String(v);
  };

  // Check if updating existing vendorContractXref or inserting new
  const existingXref = () => vendor()?.vendorContractXref;

  const { form, errors, reset, setData, data } = createForm({
    initialValues: {
      vendorId: null as number | null,
      vendorAddress: '',
      serviceTypes: [] as number[],
      paymentStatus: 'Open',
      commentDescription: '',
      otherServiceTypeText: '',
      contactName: '',
      contactEmail: '',
      phoneNumber: '',
      phoneNumberExtension: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      currency: 'USD',
      country: '',
    },
    extend: validator({
      schema: yup.object({
        serviceTypes: yup
          .mixed()
          .test(
            'at-least-one',
            'At least one Service Type is required',
            (value) => Array.isArray(value) && value.length > 0,
          ),
        paymentStatus: yup.string().required('Payment Status is required'),
        commentDescription: yup
          .string()
          .required('Comment/Description is required'),
        contactName: yup.string().required('Contact Name is required'),
        contactEmail: yup
          .string()
          .email('Invalid email format')
          .required('Contact Email is required'),
        phoneNumber: yup.string().required('Phone Number is required'),
        addressLine1: yup.string().required('Address Line 1 is required'),
        city: yup.string().required('City is required'),
        state: yup.string().required('State is required'),
        zip: yup.string().required('Zip is required'),
        currency: yup.string().required('Currency is required'),
        country: yup.string().required('Country is required'),
      }),
    }),
    onSubmit: async (values) => {
      if (localVendorName().trim() === '') {
        handleToast(ToastType.Error, 'Vendor Name is required');
        return;
      }
      if (values.addressLine1.trim() === '') {
        handleToast(ToastType.Error, 'Address Line 1 is required');
        return;
      }
      if (values.city.trim() === '') {
        handleToast(ToastType.Error, 'City is required');
        return;
      }
      if (values.state.trim() === '') {
        handleToast(ToastType.Error, 'State is required');
        return;
      }
      if (values.zip.trim() === '') {
        handleToast(ToastType.Error, 'Zip is required');
        return;
      }

      const vendorDetails: VendorDetails = {
        operationType: 'Insert',
        id: 0,
        name: localVendorName(),
        currencyType: values.currency,
        operatingLocationList: [
          {
            operationType: 'Insert',
            id: 0,
            vendorMasterId: 0,
            addressType: 'Operating',
            addressLine1: values.addressLine1,
            addressLine2: '',
            city: values.city,
            stateProvince: values.state,
            postalCode: values.zip,
            countryCode:
              mapCountryCodeForAddressAutocomplete[
                values.country as keyof typeof mapCountryCodeForAddressAutocomplete
              ] || 'USA',
            serviceTypeList: selectedServiceTypes().map((serviceTypeId) => {
              const service = vendorServiceTypes().find(
                (s) => s.id == serviceTypeId,
              );
              return {
                operationType: 'Insert',
                id: serviceTypeId,
                name: service?.name || '',
              };
            }),
            contactList: [
              {
                contactName: values.contactName,
                email: values.contactEmail,
                phone1: values.phoneNumber,
                extension1: values.phoneNumberExtension || null,
                phone2: null,
                extension2: null,
                fax: null,
                vendorAddressId: 0,
                operationType: 'Insert',
                id: 0,
                type: null,
                description: null,
                status: null,
                isDeleted: false,
                createdBy: 0,
                createdDate: new Date().toISOString(),
                modifiedBy: null,
                modifiedDate: new Date().toISOString(),
                historyContext: '',
              },
            ],
          },
        ],
        accountingSysVendorId: null,
        phoneNumber: null,
        phoneExtension: null,
        websiteURL: null,
        taxId: null,
        billingTerms: null,
        achRouting: null,
        achAccount: null,
        preferredPaymentType: null,
        preferredPaymentTypeOtherValue: null,
        addressList: [],
        vendorContracts: [],
        billingAddress: null,
        comments: [],
        physicalAddress1: null,
        physicalAddress2: null,
        physicalCity: null,
        physicalState: null,
        physicalZip: null,
        physicalCountryCode: null,
        accountingSysId: null,
      };

      try {
        setSavingNewVendor(true);
        const response = await saveNewVendorDetails(vendorDetails);
        if (response?.isSuccess) {
          setSavedVendorSelected(true);
          const createdVendor = response.value;
          const createdAddress = createdVendor.operatingLocationList?.[0];
          const createdContact = createdAddress?.contactList?.[0];

          // Clear order store vendor errors just incase
          const currentErrors = { ...orderStore.orderFormError };
          delete currentErrors[`vendors[${props.index}].description`];
          delete currentErrors[`vendors[${props.index}].serviceType`];
          delete currentErrors[`vendors[${props.index}].serviceTypeText`];
          updateOrderFormErrorState(currentErrors);
          const mappedContacts: VendorSearchContactResult[] =
            createdAddress?.contactList?.map((c) => ({
              id: c.id,
              name: c.contactName || '',
              phone: c.phone1 || null,
              phoneExtension: c.extension1 || null,
              email: c.email || null,
              isDeleted: c.isDeleted || false,
              createdDate: c.createdDate || '',
            })) || [];

          const mappedServiceTypes: VendorSearchServiceTypeResult[] =
            createdAddress?.serviceTypeList?.map((s) => ({
              id: s.id,
              name: s.name || '',
            })) || [];

          setSelectedVendorContacts(mappedContacts);
          setSelectedContact(
            mappedContacts.length > 0 ? mappedContacts[0] : null,
          );
          setSelectedVendorServiceTypes(mappedServiceTypes);

          const xref = existingXref();
          const isUpdate = xref?.id && xref.id > 0;

          updateVendorPropertyAtIndex({
            name: createdVendor.name ?? undefined,
            mailingAddress1: createdAddress?.addressLine1,
            mailingCity: createdAddress?.city,
            mailingState: createdAddress?.stateProvince,
            mailingZip: createdAddress?.postalCode,
            countryCode: createdAddress?.countryCode,
            currencyType: createdVendor.currencyType ?? undefined,
            serviceType: undefined,
            contactName: createdContact?.contactName || values.contactName,
            contactPhone: createdContact?.phone1 || values.phoneNumber,
            description: values.commentDescription,
            status: values.paymentStatus,
            draftFormState: null,
            vendorContractXref: {
              operationType: isUpdate ? 'Update' : 'Insert',
              id: isUpdate ? xref.id : 0,
              vendorMasterId: createdVendor.id,
              vendorAddressId: createdAddress?.id,
              vendorServiceTypeId: null,
              serviceTypes: mappedServiceTypes,
              contacts: mappedContacts.map((c) => ({
                id: c.id,
                name: c.name,
                phone: c.phone ?? undefined,
                phoneExt: c.phoneExtension ?? undefined,
                email: c.email ?? undefined,
              })),
            },
          });
        }
      } catch (err) {
        printLog('Error saving new vendor details:', err);
      } finally {
        setSavingNewVendor(false);
      }
    },
    onError: (formErrors) => {
      printLog('Form errors:', formErrors);
    },
  });

  createEffect(
    on(
      () =>
        [
          orderStore.activeTab.index,
          orderStore.activeTab.type,
          vendor(),
        ] as const,
      ([_index, _type, currentVendor]) => {
        if (currentVendor?.vendorContractXref?.vendorMasterId) {
          const xref = currentVendor.vendorContractXref;

          batch(() => {
            setShowVendorForm(true);
            setSavedVendorSelected(true);
            setHasSelectedAddress(true);
            setLocalVendorName(currentVendor.name || '');
            setSelectedServiceType(currentVendor.serviceType || '');
            setManuallyEnteringAddress(false);
            setSelectedServiceTypes([]);

            if (xref.serviceTypes && xref.serviceTypes.length > 0) {
              setSelectedVendorServiceTypes(
                xref.serviceTypes.map((s) => ({
                  id: s.id,
                  name: s.name,
                })),
              );
            } else if (currentVendor.serviceType) {
              setSelectedVendorServiceTypes([
                {
                  id: xref.vendorServiceTypeId || 0,
                  name: currentVendor.serviceType,
                },
              ]);
            } else {
              setSelectedVendorServiceTypes([]);
            }

            if (xref.contacts && xref.contacts.length > 0) {
              const mappedContacts: VendorSearchContactResult[] =
                xref.contacts.map((c) => ({
                  id: c.id || 0,
                  name: c.name || '',
                  phone: c.phone || null,
                  phoneExtension: c.phoneExt || null,
                  email: c.email || null,
                  isDeleted: false,
                  createdDate: '',
                }));
              setSelectedVendorContacts(mappedContacts);
              // Try to match by name first, otherwise default to first contact
              const matchedContact =
                mappedContacts.find(
                  (c) => c.name === currentVendor.contactName,
                ) || mappedContacts[0];
              setSelectedContact(matchedContact || null);
            } else if (currentVendor.contactName) {
              const contact: VendorSearchContactResult = {
                id: 0,
                name: currentVendor.contactName,
                phone: currentVendor.contactPhone || null,
                phoneExtension: null,
                email: null,
                isDeleted: false,
                createdDate: '',
              };
              setSelectedVendorContacts([contact]);
              setSelectedContact(contact);
            } else {
              setSelectedVendorContacts([]);
              setSelectedContact(null);
            }
          });

          // Defer setData calls to allow form to mount
          setTimeout(() => {
            setData(
              'vendorId',
              currentVendor.vendorContractXref?.vendorMasterId ?? null,
            );
            setData('addressLine1', currentVendor.mailingAddress1 || '');
            setData('city', currentVendor.mailingCity || '');
            setData('state', currentVendor.mailingState || '');
            setData('zip', currentVendor.mailingZip || '');
            setData('country', currentVendor.countryCode || '');
            setData('currency', currentVendor.currencyType || '');
            setData('paymentStatus', currentVendor.status || '');
            setData('commentDescription', currentVendor.description || '');
            setData('contactName', currentVendor.contactName || '');
            setData('phoneNumber', currentVendor.contactPhone || '');
            setData(
              'otherServiceTypeText',
              currentVendor.serviceTypeText || '',
            );
          }, 0);
        } else if (currentVendor?.draftFormState) {
          // Vendor has draft state, restore it
          const draftState = currentVendor.draftFormState;
          batch(() => {
            setShowVendorForm(draftState.showVendorForm ?? false);
            setLocalVendorName(draftState.localVendorName ?? '');
            setHasSelectedAddress(draftState.hasSelectedAddress ?? false);
            setManuallyEnteringAddress(
              draftState.manuallyEnteringAddress ?? false,
            );
            setSelectedServiceTypes(draftState.selectedServiceTypes ?? []);
            setSavedVendorSelected(false);
            setSelectedServiceType('');
            setSelectedVendorContacts([]);
            setSelectedContact(null);
            setSelectedVendorServiceTypes([]);
          });

          setTimeout(() => {
            setData('contactName', draftState.contactName ?? '');
            setData('contactEmail', draftState.contactEmail ?? '');
            setData('phoneNumber', draftState.phoneNumber ?? '');
            setData(
              'phoneNumberExtension',
              draftState.phoneNumberExtension ?? '',
            );
            setData('addressLine1', draftState.addressLine1 ?? '');
            setData('addressLine2', draftState.addressLine2 ?? '');
            setData('city', draftState.city ?? '');
            setData('state', draftState.state ?? '');
            setData('zip', draftState.zip ?? '');
            setData('currency', draftState.currency ?? 'USD');
            setData('country', draftState.country ?? '');
            setData('paymentStatus', draftState.paymentStatus ?? 'Open');
            setData('commentDescription', draftState.commentDescription ?? '');
            setData('serviceTypes', draftState.selectedServiceTypes ?? []);
          }, 0);
        } else {
          // No saved vendor and no draft, reset the form
          batch(() => {
            reset();
            setShowVendorForm(false);
            setSavedVendorSelected(false);
            setHasSelectedAddress(false);
            setLocalVendorName('');
            setSelectedServiceType('');
            setSelectedVendorContacts([]);
            setSelectedContact(null);
            setSelectedVendorServiceTypes([]);
            setManuallyEnteringAddress(false);
            setSelectedServiceTypes([]);
          });
        }
      },
      { defer: false },
    ),
  );

  const handleCreateNewVendor = () => {
    setShowVendorForm(true);
    updateVendorDraftFormState({ showVendorForm: true });
  };

  const handleClearSearch = () => {
    searchFormReset();
    setHasSearched(false);
    setVendorResults([]);
  };

  const clearVendorState = (keepFormOpen: boolean) => {
    setSavedVendorSelected(false);
    setShowVendorForm(keepFormOpen);
    setSelectedVendorContacts([]);
    setSelectedContact(null);
    setSelectedVendorServiceTypes([]);
    setSelectedServiceTypes([]);
    setSelectedServiceType('');
    setLocalVendorName('');
    setHasSelectedAddress(false);
    reset();
    updateVendorPropertyAtIndex({
      name: '',
      mailingAddress1: '',
      mailingCity: '',
      mailingState: '',
      mailingZip: '',
      countryCode: '',
      currencyType: '',
      serviceType: '',
      serviceTypeText: null,
      contactName: '',
      contactPhone: '',
      description: '',
      status: 'Open',
      draftFormState: null,
      vendorContractXref: {
        vendorMasterId: null,
      },
    });
  };

  const fetchVendors = async ({
    vendorName,
    vendorAddress,
    vendorLocationDisplay: _vendorLocationDisplay,
    vendorZip,
    vendorCity,
    vendorState,
    vendorCountry,
    vendorLat,
    vendorLong,
    searchCountry: _searchCountry,
    serviceTypes,
  }: {
    vendorName: string;
    vendorAddress: string;
    vendorLocationDisplay: string;
    vendorZip: string;
    vendorCity: string;
    vendorState: string;
    vendorCountry: string;
    vendorLat: number | null;
    vendorLong: number | null;
    searchCountry: string;
    serviceTypes: number[];
  }) => {
    setIsSearching(true);
    setHasSearched(true);

    const params: Record<string, unknown> = {};
    if (vendorName) params.name = vendorName;
    if (vendorAddress) params.address = vendorAddress;
    if (vendorZip) params.zip = vendorZip;
    if (vendorCity) params.city = vendorCity;
    if (vendorState) params.state = vendorState;
    if (vendorCountry) params.country = vendorCountry;
    if (vendorLat !== null) params.latitude = vendorLat;
    if (vendorLong !== null) params.longitude = vendorLong;
    if (serviceTypes.length > 0) params.serviceTypes = serviceTypes;

    try {
      const response = await searchVendors(params);
      setVendorResults(response?.isSuccess ? response.value : []);
    } finally {
      setIsSearching(false);
    }
  };

  const formattedAddress = () => {
    const line2 = [data().city, data().state, data().zip, data().country]
      .filter(Boolean)
      .join(', ');
    return (
      <Box style={{ 'font-weight': 500 }}>
        {data().addressLine1 && (
          <>
            {data().addressLine1}
            <br />
          </>
        )}
        {line2}
      </Box>
    );
  };

  const {
    form: searchForm,
    errors: searchFormErrors,
    reset: searchFormReset,
    setData: setSearchFormData,
    data: searchFormData,
    isSubmitting,
  } = createForm({
    initialValues: {
      vendorName: '',
      vendorAddress: '',
      vendorLocationDisplay: '',
      vendorZip: '',
      vendorCity: '',
      vendorState: '',
      vendorCountry: '',
      vendorLat: null as number | null,
      vendorLong: null as number | null,
      searchCountry: 'US',
      serviceTypes: [] as number[],
    },
    onSubmit: fetchVendors,
  });

  const formatSelectError = (
    error: string | string[] | null | undefined,
  ): string | null | undefined => {
    if (error == null) return undefined;
    if (Array.isArray(error)) {
      const messages = error.filter(Boolean);
      return messages.length > 0 ? messages.join(', ') : undefined;
    }
    return error;
  };

  const handleSearchServiceTypeChange = (value: string) => {
    const numValue = Number(value);
    if (searchFormData().serviceTypes.includes(numValue)) return;
    setSearchFormData('serviceTypes', [
      ...searchFormData().serviceTypes,
      numValue,
    ]);
  };

  const handleVendorServiceTypeChange = (value: string) => {
    const numValue = Number(value);
    if (selectedServiceTypes().includes(numValue)) return;
    const newValue = [...selectedServiceTypes(), numValue];
    setSelectedServiceTypes(newValue);
    setData('serviceTypes', newValue);
    updateVendorDraftFormState({
      selectedServiceTypes: newValue,
    });
  };

  const handleVendorSelect = (
    newVendor: VendorSearchResult,
    selectedServiceType?: VendorSearchServiceTypeResult,
  ) => {
    const sortedContacts = newVendor.contacts
      .filter((c) => !c.isDeleted)
      .sort((a, b) => {
        const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
        const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
        return dateB - dateA;
      });
    // Show the form first so it renders
    setShowVendorForm(true);
    setLocalVendorName(newVendor.name);
    setSelectedVendorContacts(sortedContacts);
    setSelectedContact(sortedContacts.length > 0 ? sortedContacts[0] : null);
    setSelectedVendorServiceTypes(newVendor.serviceTypes);

    setSavedVendorSelected(true);
    setHasSelectedAddress(true);
    setManuallyEnteringAddress(false);
    handleClearSearch();

    setTimeout(() => {
      setData('vendorId', newVendor.vendorId);
      setData('addressLine1', newVendor.addressLine1);
      setData('city', newVendor.city);
      setData('state', newVendor.state);
      setData('zip', newVendor.postalCode);
      setData('country', newVendor.countryCode);
      setData('currency', newVendor.currencyType);
      setData('paymentStatus', 'Open');
      setData('commentDescription', '');
      setData('otherServiceTypeText', '');
      setData('contactName', sortedContacts[0]?.name || '');
      setData(
        'phoneNumber',
        sortedContacts[0]?.phone
          ? formatPhoneNumber(sortedContacts[0].phone!)
          : '',
      );
      setSelectedServiceType(selectedServiceType?.name || '');
      updateVendorPropertyAtIndex({
        serviceType: selectedServiceType?.name || '',
        serviceTypeText: '',
        description: '',
        vendorContractXref: {
          ...vendor()?.vendorContractXref,
          vendorServiceTypeId: selectedServiceType?.id || null,
          operationType: 'Update',
        },
      });
    }, 0);

    const xref = existingXref();
    const isUpdate = xref?.id && xref.id > 0;

    updateVendorPropertyAtIndex({
      name: newVendor.name,
      mailingAddress1: newVendor.addressLine1,
      mailingCity: newVendor.city,
      mailingState: newVendor.state,
      mailingZip: newVendor.postalCode,
      countryCode: newVendor.countryCode,
      currencyType: newVendor.currencyType,
      serviceType: newVendor.serviceTypes[0]?.name || '',
      contactName: sortedContacts[0]?.name || '',
      contactPhone: sortedContacts[0]?.phone || '',
      status: 'Open',
      vendorContractXref: {
        operationType: isUpdate ? 'Update' : 'Insert',
        id: isUpdate ? xref.id : 0,
        vendorMasterId: newVendor.vendorId,
        vendorAddressId: newVendor.addressId,
        vendorServiceTypeId: newVendor.serviceTypes[0]?.id || null,
        serviceTypes: newVendor.serviceTypes.map((s) => ({
          id: s.id,
          name: s.name,
        })),
        contacts: sortedContacts.map((c) => ({
          id: c.id,
          name: c.name,
          phone: c.phone ?? undefined,
          phoneExt: c.phoneExtension ?? undefined,
          email: c.email ?? undefined,
        })),
      },
    });
  };

  return (
    <Grid item size={12}>
      <Grid item container spacing={2}>
        <Grid item size={12}>
          <form ref={searchForm}>
            <Grid item container spacing={2}>
              <Grid item size={12}>
                <Typography
                  variant="body1"
                  component="p"
                  class="text-base! pb font-roboto! font-medium! text-[#123B50]!"
                >
                  Vendor Search
                </Typography>
              </Grid>
              <Grid item size={12} container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                  <TextField
                    label="Name"
                    value={searchFormData().vendorName}
                    class="flex-1!"
                    onChange={(e: string) => setSearchFormData('vendorName', e)}
                    error={searchFormErrors().vendorName}
                    dataTestId="vendor-name-search-input"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
                  <TextField
                    label="Operating Address"
                    value={searchFormData().vendorAddress}
                    onChange={(e: string) =>
                      setSearchFormData('vendorAddress', e)
                    }
                    error={searchFormErrors().vendorAddress}
                    class="flex-1!"
                    dataTestId="vendor-address-search-input"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 1.5 }}>
                  <TextField
                    label="City"
                    value={searchFormData().vendorCity}
                    class="flex-1!"
                    onChange={(e: string) => setSearchFormData('vendorCity', e)}
                    error={searchFormErrors().vendorCity}
                    dataTestId="vendor-city-search-input"
                  />
                </Grid>
                <Grid item size={{ xs: 6, sm: 3, md: 2, lg: 3, xl: 1 }}>
                  <TextField
                    label="State"
                    value={searchFormData().vendorState}
                    class="flex-1!"
                    onChange={(e: string) => {
                      const sanitized = sanitizeStateInput(e);
                      if (sanitized === searchFormData().vendorState) return;
                      setSearchFormData('vendorState', sanitized);
                    }}
                    error={searchFormErrors().vendorState}
                    maxLength={2}
                    dataTestId="vendor-state-search-input"
                  />
                </Grid>
                <Grid item size={{ xs: 6, sm: 3, md: 2, lg: 3, xl: 1 }}>
                  <SelectInput
                    label="Zip Country"
                    menuItems={countries.map((c) => ({
                      label: c.label,
                      value: c.value,
                    }))}
                    value={searchFormData().searchCountry}
                    onChange={(newCountry) => {
                      if (newCountry !== searchFormData().searchCountry) {
                        setSearchFormData('vendorLocationDisplay', '');
                        setSearchFormData('vendorZip', '');
                        setSearchFormData('vendorCity', '');
                        setSearchFormData('vendorState', '');
                        setSearchFormData('vendorCountry', '');
                        setSearchFormData('vendorLat', null);
                        setSearchFormData('vendorLong', null);
                      }
                      setSearchFormData('searchCountry', newCountry);
                    }}
                    dataTestId={formatDataTestId('Zip Country', 'selectInput')}
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 2 }}>
                  <AutoComplete
                    endpoint={`capacity/autocomplete?country=${searchFormData().searchCountry}&term`}
                    version="v4"
                    responseFieldName="display"
                    onItemSelect={(item) => {
                      const itemTyped = item as LocationSelect;
                      setSearchFormData(
                        'vendorLocationDisplay',
                        itemTyped.display || '',
                      );
                      setSearchFormData('vendorZip', itemTyped.zipcode || '');
                      setSearchFormData('vendorCity', itemTyped.city || '');
                      setSearchFormData('vendorState', itemTyped.state || '');
                      setSearchFormData(
                        'vendorCountry',
                        itemTyped.country || '',
                      );
                      setSearchFormData('vendorLat', itemTyped.lat ?? null);
                      setSearchFormData('vendorLong', itemTyped.long ?? null);
                    }}
                    onClearValue={() => {
                      setSearchFormData('vendorLocationDisplay', '');
                      setSearchFormData('vendorZip', '');
                      setSearchFormData('vendorCity', '');
                      setSearchFormData('vendorState', '');
                      setSearchFormData('vendorCountry', '');
                      setSearchFormData('vendorLat', null);
                      setSearchFormData('vendorLong', null);
                    }}
                    id="autocomplete-vendor-zip"
                    label="Operating Zip"
                    placeholder="City, State or ZIP"
                    defaultValue=""
                    value={searchFormData().vendorLocationDisplay}
                    dataTestId="vendor-zip-search-input"
                  />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 5, xl: 12 }}>
                  <SelectInputCustom
                    dataTestId="vendor-service-types-select"
                    label="Service Types"
                    menuItems={vendorServiceTypes()
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((service) => ({
                        label: service.name,
                        value: String(service.id),
                      }))}
                    multiple
                    value={searchFormData().serviceTypes.map(String)}
                    onChange={handleSearchServiceTypeChange}
                    renderValue={(selected) => {
                      const values = Array.isArray(selected)
                        ? selected
                        : [selected];
                      return (
                        <Box
                          style={{
                            display: 'flex',
                            'flex-wrap': 'wrap',
                            'margin-left': '-8px',
                          }}
                          gap={1}
                        >
                          {values.map((val) => {
                            const numValue = Number(val);
                            const service = vendorServiceTypes().find(
                              (s) => s.id === numValue,
                            );
                            return (
                              <Chip
                                id={`chip-service-${val}`}
                                text={String(service?.name || val)}
                                variant="filled"
                                isDeleteIcon
                                deleteIconColor="white"
                                class="text-white! bg-[#026EA1]! rounded h-[25px]"
                                onDelete={() => {
                                  setSearchFormData(
                                    'serviceTypes',
                                    searchFormData().serviceTypes.filter(
                                      (item) => item !== numValue,
                                    ),
                                  );
                                }}
                              />
                            );
                          })}
                        </Box>
                      );
                    }}
                    error={formatSelectError(searchFormErrors().serviceTypes)}
                  />
                </Grid>
              </Grid>
              <Grid item size={12} class="flex justify-between">
                <Button
                  variant="text"
                  label="Create New Vendor"
                  onClick={() => {
                    if (savedVendorSelected()) {
                      openDialogBox('create-vendor-confirmation-dialog');
                    } else {
                      handleCreateNewVendor();
                    }
                  }}
                  disabled={savingNewVendor()}
                  dataTestId="create-new-vendor-button"
                />
                <Box display="flex" gap={1}>
                  <Button
                    variant="text"
                    label="Clear Search"
                    onClick={handleClearSearch}
                    disabled={
                      !searchFormData().vendorName &&
                      !searchFormData().vendorAddress &&
                      !searchFormData().vendorZip &&
                      searchFormData().serviceTypes.length === 0
                    }
                    dataTestId="clear-vendor-search-button"
                  />
                  <Button
                    variant="contained"
                    startIcon={<MagnifyIcon size={20} />}
                    label="Search Vendors"
                    dataTestId="search-vendors-button"
                    type="submit"
                    isLoading={isSubmitting()}
                    disabled={
                      isSubmitting() ||
                      (!searchFormData().vendorName &&
                        !searchFormData().vendorCity &&
                        !searchFormData().vendorState &&
                        !searchFormData().vendorAddress &&
                        !searchFormData().vendorZip &&
                        searchFormData().serviceTypes.length === 0)
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </form>
        </Grid>
        <Show when={hasSearched()}>
          <VendorSearchResultGridOnLoad
            vendorData={vendorResults}
            isLoading={isSearching}
            savedVendorSelected={savedVendorSelected()}
            vendorName={localVendorName()}
            formOpen={showVendorForm()}
            onVendorSelect={handleVendorSelect}
          />
        </Show>
        <Show when={showVendorForm()}>
          <Grid item size={12}>
            <Divider />
          </Grid>
          <Grid item size={12}>
            <Box display="flex" alignItems="center" justifyContent="left">
              <Typography
                variant="body1"
                component="p"
                class="text-base! font-roboto! font-medium! text-[#123B50]!"
              >
                {savedVendorSelected() ? 'Selected Vendor' : 'Add New Vendor'}
              </Typography>
              <Show when={savedVendorSelected()}>
                <Button
                  variant="text"
                  color="error"
                  label="Clear Vendor"
                  onClick={() => {
                    openDialogBox('clear-vendor-dialog');
                  }}
                  dataTestId="vendors-clear-vendor-button"
                />
              </Show>
            </Box>
          </Grid>
          <Grid item size={12}>
            <form
              ref={form}
              style={{
                width: 'inherit',
              }}
            >
              <Grid item size={12} container spacing={2}>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Stack spacing={3}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AutoComplete
                        id={`vendor-name-autocomplete-${orderStore.activeTab.index}`}
                        disabled={savingNewVendor() || savedVendorSelected()}
                        endpoint={'vendor/search?name'}
                        version="v4"
                        defaultValue=""
                        required
                        label="Vendor Name"
                        placeholder="Enter a Vendor"
                        innerRefClass="flex-1!"
                        value={localVendorName()}
                        onChange={(e) => {
                          setLocalVendorName(e);
                          if (!savedVendorSelected()) {
                            updateVendorDraftFormState({ localVendorName: e });
                          }
                        }}
                        onClearValue={() => {
                          printLog('Clearing Vendor selection');
                          setLocalVendorName('');
                          if (!savedVendorSelected()) {
                            updateVendorDraftFormState({ localVendorName: '' });
                          }
                        }}
                        onItemSelect={(item: VendorSearchResult) => {
                          handleVendorSelect(item);
                        }}
                        renderItem={(item: VendorSearchResult) => {
                          const vendorItem = item;
                          return (
                            <Box class="flex items-center gap-3 w-full">
                              <StorefrontIcon class="text-[#026EA1]!" />
                              <Box class="flex-1">
                                <Box class="flex items-center gap-1">
                                  <Typography
                                    variant="body2"
                                    component="span"
                                    class="font-roboto! font-normal! text-[#123B50]! text-sm!"
                                  >
                                    {vendorItem.name}
                                  </Typography>
                                </Box>
                                <Show when={vendorItem.fullAddress}>
                                  <Typography
                                    variant="body2"
                                    component="p"
                                    class="font-roboto! font-normal! text-[#00000099]! text-xs!"
                                  >
                                    {vendorItem.fullAddress}
                                  </Typography>
                                </Show>
                              </Box>
                              <Show when={vendorItem.atgVerified}>
                                <ClipboardCheckIcon class="text-[#28957C]!" />
                              </Show>
                            </Box>
                          );
                        }}
                      />
                      <Button
                        variant="contained"
                        label="Profile"
                        startIcon={<OpenInNewIcon size={20} />}
                        disabled={data().vendorId === null || savingNewVendor()}
                        onClick={() => {
                          window.open(
                            `/vendors/details/${data().vendorId}`,
                            '_blank',
                          );
                        }}
                        dataTestId="vendor-profile-button"
                      />
                    </Box>
                    <SelectInput
                      label="Currency"
                      menuItems={currencyTypes.map((c) => ({
                        label: c,
                        value: c,
                      }))}
                      value={data().currency}
                      onChange={(value) => {
                        setData('currency', value);
                        updateVendorDraftFormState({
                          currency: value,
                        });
                      }}
                      required
                      error={felteFieldError(errors().currency)}
                      disabled={
                        savingNewVendor() ||
                        savedVendorSelected() ||
                        !userHasRole(UserRole.Accounting)
                      }
                      dataTestId={formatDataTestId('Currency', 'selectInput')}
                    />
                    <SelectInput
                      label="Payment Status*"
                      menuItems={vendorStatusTypes.map((s) => ({
                        label: s,
                        value: s,
                      }))}
                      required
                      value={data().paymentStatus}
                      onChange={(value) => {
                        setData('paymentStatus', value);
                        updateVendorDraftFormState({
                          paymentStatus: value,
                        });
                        if (savedVendorSelected()) {
                          updateVendorPropertyAtIndex({
                            status: value,
                          });
                        }
                      }}
                      error={felteFieldError(errors().paymentStatus)}
                      disabled={
                        savingNewVendor() || !userHasRole(UserRole.Accounting)
                      }
                      dataTestId={formatDataTestId(
                        'Payment Status',
                        'selectInput',
                      )}
                    />
                    <TextField
                      label="Comment/Description"
                      multiline
                      rows={5}
                      value={data().commentDescription}
                      onChange={(e: string) => {
                        setData('commentDescription', e);
                        updateVendorDraftFormState({ commentDescription: e });
                        if (savedVendorSelected()) {
                          updateVendorPropertyAtIndex({
                            description: e,
                          });
                        }
                      }}
                      required
                      error={
                        errors().commentDescription ??
                        (savedVendorSelected() &&
                          orderStore.orderFormError &&
                          orderStore.orderFormError[
                            `vendors[${props.index}].description`
                          ])
                      }
                      dataTestId="vendor-comment-description"
                      disabled={savingNewVendor()}
                    />
                  </Stack>
                </Grid>
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Grid item size={12}>
                    <Stack spacing={3}>
                      <Show when={savedVendorSelected()}>
                        <ContactDropdown
                          variant="outlined"
                          contacts={
                            selectedVendorContacts().map((c) => ({
                              id: c.id,
                              objectid: c.id,
                              name: c.name,
                              phone1: c.phone || '',
                              email: c.email || '',
                              mailerEmails: false,
                              verificationStatus: 0,
                              emailVerified: false,
                              phoneVerified: false,
                              ownershipSource: 0,
                            })) as CarrierContactT[]
                          }
                          onChange={(contactId) => {
                            const contact = selectedVendorContacts().find(
                              (c) => c.id === contactId,
                            );
                            if (contact) {
                              setSelectedContact(contact);
                              updateVendorPropertyAtIndex({
                                contactName: contact.name,
                                contactPhone: contact.phone || '',
                              });
                            }
                          }}
                          contactId={selectedContact()?.id}
                          disabled={savingNewVendor()}
                          respectVerification={false}
                        />
                        <Show when={selectedContact()}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                            style={{
                              'font-size': '14px',
                              'font-weight': 600,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <PhoneIcon size={'16px'} color="#026EA1" />
                              <span style={{ color: '#666' }}>
                                {formatPhoneNumber(
                                  selectedContact()?.phone ?? undefined,
                                  undefined,
                                  selectedContact()?.phoneExtension,
                                ) || '-'}
                              </span>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <EmailIcon size={'16px'} color="#026EA1" />
                              <span style={{ color: '#666' }}>
                                {selectedContact()?.email || '-'}
                              </span>
                            </Box>
                          </Box>
                        </Show>
                      </Show>
                      <Show when={!savedVendorSelected()}>
                        <TextField
                          label="Contact Name"
                          value={data().contactName}
                          required
                          onChange={(e: string) => {
                            setData('contactName', e);
                            updateVendorDraftFormState({ contactName: e });
                          }}
                          error={errors().contactName}
                          dataTestId="vendor-contact-name"
                          disabled={savingNewVendor()}
                        />
                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="space-between"
                        >
                          <PhoneInput
                            label="Phone Number"
                            value={data().phoneNumber}
                            required
                            onChange={(e: string) => {
                              setData('phoneNumber', e);
                              updateVendorDraftFormState({ phoneNumber: e });
                            }}
                            error={errors().phoneNumber}
                            dataTestId="vendor-phone-number"
                            disabled={savingNewVendor()}
                          />
                          <TextField
                            label="Extension"
                            value={data().phoneNumberExtension}
                            onChange={(e: string) => {
                              setData('phoneNumberExtension', e);
                              updateVendorDraftFormState({
                                phoneNumberExtension: e,
                              });
                            }}
                            error={errors().phoneNumberExtension}
                            dataTestId="vendor-phone-number-extension"
                            maxLength={10}
                            disabled={savingNewVendor()}
                          />
                        </Box>
                        <TextField
                          label="Email"
                          value={data().contactEmail}
                          required
                          onChange={(e: string) => {
                            setData('contactEmail', e);
                            updateVendorDraftFormState({ contactEmail: e });
                          }}
                          error={errors().contactEmail}
                          dataTestId="vendor-contact-email"
                          disabled={savingNewVendor()}
                          maxLength={200}
                        />
                      </Show>
                      <Show when={!savedVendorSelected()}>
                        <SelectInputCustom
                          dataTestId="vendor-service-types-select"
                          label="Service Type(s)*"
                          menuItems={vendorServiceTypes()
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((service) => ({
                              label: service.name,
                              value: String(service.id),
                            }))}
                          multiple
                          disabled={savingNewVendor()}
                          value={selectedServiceTypes().map(String)}
                          onChange={handleVendorServiceTypeChange}
                          renderValue={(selected) => {
                            const values = Array.isArray(selected)
                              ? selected
                              : [selected];
                            return (
                              <Box
                                style={{
                                  display: 'flex',
                                  'flex-wrap': 'wrap',
                                  'margin-left': '-8px',
                                }}
                                gap={1}
                              >
                                {values.map((val) => {
                                  const value = Number(val);
                                  const service = vendorServiceTypes().find(
                                    (s) => s.id === value,
                                  );
                                  return (
                                    <span
                                      onMouseDown={(e) => e.stopPropagation()}
                                    >
                                      <Chip
                                        id={`chip-vendor-service-${value}`}
                                        text={String(service?.name || value)}
                                        variant="filled"
                                        isDeleteIcon
                                        deleteIconColor="white"
                                        class="text-white! bg-[#026EA1]! rounded h-[25px]"
                                        onDelete={() => {
                                          const newValue =
                                            selectedServiceTypes().filter(
                                              (item) => item !== value,
                                            );
                                          setSelectedServiceTypes(newValue);
                                          setData('serviceTypes', newValue);
                                          updateVendorDraftFormState({
                                            selectedServiceTypes: newValue,
                                          });
                                        }}
                                      />
                                    </span>
                                  );
                                })}
                              </Box>
                            );
                          }}
                          error={formatSelectError(errors().serviceTypes)}
                          required
                        />
                      </Show>
                      <Show when={savedVendorSelected()}>
                        <SelectInput
                          label="Service Type*"
                          menuItems={selectedVendorServiceTypes().map(
                            (service) => ({
                              label: service.name,
                              value: service.name,
                            }),
                          )}
                          value={selectedServiceType()}
                          onChange={(newServiceType) => {
                            setSelectedServiceType(newServiceType);
                            const serviceTypeObj =
                              selectedVendorServiceTypes().find(
                                (s) => s.name === newServiceType,
                              );
                            updateVendorPropertyAtIndex({
                              serviceType: newServiceType,
                              vendorContractXref: {
                                ...vendor()?.vendorContractXref,
                                vendorServiceTypeId: serviceTypeObj?.id || null,
                                operationType: 'Update',
                              },
                            });
                          }}
                          disabled={savingNewVendor()}
                          required
                          error={
                            orderStore.orderFormError &&
                            orderStore.orderFormError[
                              `vendors[${props.index}].serviceType`
                            ]
                              ? felteFieldError(
                                  orderStore.orderFormError[
                                    `vendors[${props.index}].serviceType`
                                  ],
                                )
                              : undefined
                          }
                          dataTestId={formatDataTestId(
                            'Service Type',
                            'selectInput',
                          )}
                        />
                        <Show when={selectedServiceType() == 'Other'}>
                          <TextField
                            label="Add text"
                            value={data().otherServiceTypeText}
                            onChange={(e: string) => {
                              setData('otherServiceTypeText', e);
                              if (savedVendorSelected()) {
                                updateVendorPropertyAtIndex({
                                  serviceTypeText: e,
                                });
                              }
                            }}
                            error={
                              orderStore.orderFormError &&
                              orderStore.orderFormError[
                                `vendors[${props.index}].serviceTypeText`
                              ]
                            }
                            disabled={savingNewVendor()}
                            dataTestId="add-text-textInput"
                          />
                        </Show>
                      </Show>

                      <Show when={manuallyEnteringAddress()}>
                        <TextField
                          label="Address Line 1"
                          value={data().addressLine1}
                          onChange={(e: string) => {
                            setData('addressLine1', e);
                            updateVendorDraftFormState({ addressLine1: e });
                          }}
                          required
                          error={errors().addressLine1}
                          dataTestId="vendor-address-line-1"
                          maxLength={200}
                          disabled={savingNewVendor()}
                        />
                        <TextField
                          label="Address Line 2"
                          value={data().addressLine2}
                          onChange={(e: string) => {
                            setData('addressLine2', e);
                            updateVendorDraftFormState({ addressLine2: e });
                          }}
                          error={errors().addressLine2}
                          dataTestId="vendor-address-line-2"
                          disabled={savingNewVendor()}
                          maxLength={200}
                        />
                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="space-between"
                        >
                          <TextField
                            label="City"
                            value={data().city}
                            onChange={(e: string) => {
                              setData('city', e);
                              updateVendorDraftFormState({ city: e });
                            }}
                            required
                            error={errors().city}
                            dataTestId="vendor-city"
                            disabled={savingNewVendor()}
                            maxLength={200}
                          />
                          <TextField
                            label="State"
                            value={data().state}
                            onChange={(e: string) => {
                              const sanitized = sanitizeStateInput(e);
                              if (sanitized === data().state) return;
                              setData('state', sanitized);
                              updateVendorDraftFormState({ state: sanitized });
                            }}
                            required
                            error={errors().state}
                            dataTestId="vendor-state"
                            disabled={savingNewVendor()}
                            maxLength={2}
                          />
                          <TextField
                            label="Zip"
                            value={data().zip}
                            onChange={(e: string) => {
                              setData('zip', e);
                              updateVendorDraftFormState({ zip: e });
                            }}
                            required
                            error={errors().zip}
                            dataTestId="vendor-zip"
                            disabled={savingNewVendor()}
                            maxLength={20}
                          />
                        </Box>
                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="space-between"
                        >
                          <SelectInput
                            label="Country"
                            menuItems={countryTypes.map((c) => ({
                              label: c.label,
                              value: String(c.value),
                            }))}
                            value={data().country}
                            onChange={(value) => {
                              setData('country', value);
                              updateVendorDraftFormState({
                                country: value,
                              });
                            }}
                            required
                            disabled={savingNewVendor()}
                            error={felteFieldError(errors().country)}
                            dataTestId={formatDataTestId(
                              'Country',
                              'selectInput',
                            )}
                          />
                        </Box>
                      </Show>

                      <Show
                        when={
                          !hasSelectedAddress() && !manuallyEnteringAddress()
                        }
                      >
                        <AddressAutoComplete
                          label="Address"
                          disabled={savingNewVendor()}
                          placeholder="Search for an address"
                          onItemSelect={(item) => {
                            printLog('Selected Address Item:', item);
                            setData('addressLine1', item.address1!);
                            setData('city', item.city!);
                            setData('state', item.state!);
                            setData('zip', item.zipCode!);
                            setData('country', item.country!);
                            setHasSelectedAddress(true);
                            updateVendorDraftFormState({
                              addressLine1: item.address1!,
                              city: item.city!,
                              state: item.state!,
                              zip: item.zipCode!,
                              country: item.country!,
                              hasSelectedAddress: true,
                            });
                          }}
                          error={errors().addressLine1}
                        />
                        <Button
                          variant="text"
                          label="Enter Address Manually"
                          onClick={() => {
                            setManuallyEnteringAddress(true);
                            updateVendorDraftFormState({
                              manuallyEnteringAddress: true,
                            });
                          }}
                          disabled={savingNewVendor()}
                          sx={{
                            'justify-content': 'flex-start',
                            'padding-left': 0,
                            width: 'fit-content',
                          }}
                          dataTestId="vendors-enter-address-manually-button"
                        />
                      </Show>
                      <Show when={hasSelectedAddress()}>
                        {formattedAddress()}
                      </Show>
                      <Show when={!savedVendorSelected()}>
                        <Box
                          display="flex"
                          justifyContent="end"
                          alignItems="end"
                          gap={1}
                        >
                          <Button
                            variant="text"
                            label="Clear Info"
                            onClick={() => {
                              reset();
                              setHasSelectedAddress(false);
                              setLocalVendorName('');
                              setSelectedServiceTypes([]);
                              setManuallyEnteringAddress(false);
                              setSelectedContact(null);
                              setSelectedVendorContacts([]);
                              clearVendorDraftFormState();
                            }}
                            disabled={savingNewVendor()}
                            dataTestId="vendors-clear-new-vendor-form-button"
                          />
                          <Button
                            variant="contained"
                            type="submit"
                            label="Add Vendor"
                            disabled={savingNewVendor()}
                            dataTestId="vendors-add-vendor"
                          />
                        </Box>
                      </Show>
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Show>
      </Grid>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen('clear-vendor-dialog')}
        dataTestId="clear-vendor-dialog"
        id="clear-vendor-dialog"
        title={
          (
            <Typography
              variant="h6"
              component="div"
              class="font-roboto! font-medium! text-[#123B50]!"
            >
              Remove selected vendor?
            </Typography>
          ) as unknown as string
        }
        onSubmit={() => {
          clearVendorState(false);
          closeDialogBox('clear-vendor-dialog');
        }}
        onSubmitText="Clear Vendor"
        onSubmitBtnClass="bg-[#B00020]!"
        onCancel={() => {
          closeDialogBox('clear-vendor-dialog');
        }}
        closeDialogBox={(_reason) => {
          closeDialogBox('clear-vendor-dialog');
        }}
      >
        <Typography
          variant="body2"
          component="p"
          class="font-sm! font-roboto! text-[#000000e3]!"
        >
          This will remove the currently selected vendor. You'll need to search
          for a new vendor or create a new vendor profile before you can finish
          setting up a vendor for the load.
        </Typography>
      </ConfirmationDialogBox>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(
          'create-vendor-confirmation-dialog',
        )}
        dataTestId="create-vendor-confirmation-dialog"
        id="create-vendor-confirmation-dialog"
        title={
          (
            <Typography
              variant="h6"
              component="div"
              class="font-roboto! font-medium! text-[#123B50]!"
            >
              Replace vendor with a new vendor?
            </Typography>
          ) as unknown as string
        }
        onSubmit={() => {
          clearVendorState(true);
          closeDialogBox('create-vendor-confirmation-dialog');
        }}
        onSubmitText="Clear Vendor"
        onSubmitBtnClass="bg-[#B00020]!"
        onCancel={() => {
          closeDialogBox('create-vendor-confirmation-dialog');
        }}
        closeDialogBox={(_reason) => {
          closeDialogBox('create-vendor-confirmation-dialog');
        }}
      >
        <Typography
          variant="body2"
          component="p"
          class="font-sm! font-roboto! text-[#000000e3]!"
        >
          This will remove the currently selected vendor and you will be able to
          manually enter details for a new vendor.
        </Typography>
      </ConfirmationDialogBox>
    </Grid>
  );
};
